import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { ACCESS_GROUPS } from '../../types/user';
import { BarChart3, Folder, Users, AlertTriangle, MapPin, UserCheck, Settings, User, LogOut, ChevronDown, ChevronLeft, ChevronRight, Moon, Sun, Database, FileText, Home, Layers, Shield } from 'lucide-react';

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isLoading, isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { isCollapsed, toggleCollapse } = useSidebar();
  const [isSettingsDropdownOpen, setIsSettingsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Helper function to check if a route is active
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // Role-group gate for menu items. Mirrors backend ACCESS_GROUPS:
  // super_admin always passes; otherwise the user must hold a group role.
  const inGroup = (group: readonly string[]): boolean => {
    if (isLoading || !isAuthenticated) return false;
    const roleName = user?.role?.name;
    if (!roleName) return false;
    if (roleName === 'super_admin') return true;
    return group.includes(roleName);
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch {
      // Logout failure is non-fatal; ignore.
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsSettingsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`${isCollapsed ? 'w-20' : 'w-80'} bg-dark-blue shadow-lg h-screen flex flex-col fixed left-0 top-0 z-10 overflow-y-auto transition-all duration-300 ease-in-out`}>
      {/* Top Section: Back to Home & Toggle Button */}
      <div className={`p-4 border-b border-gray-300 dark:border-gray-700 flex ${isCollapsed ? 'flex-col items-center space-y-4' : 'justify-between items-center'}`}>
        <Link
          to="/"
          className="p-2 rounded-lg hover:bg-gray-600 transition-colors duration-200 text-white"
          title="Retour à l'accueil"
          aria-label="Back to home"
        >
          <Home className="w-5 h-5" />
        </Link>
        <button
          onClick={toggleCollapse}
          className="p-2 rounded-lg hover:bg-gray-600 transition-colors duration-200 text-white"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <ChevronLeft className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* User Profile Section */}
      {!isCollapsed && (
        <div className="p-4 border-b border-gray-300 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary-purple rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-white">
                {user ? (() => {
                  // Check if firstName and lastName are null/undefined
                  const hasFirstName = user.firstName && user.firstName.trim() !== '';
                  const hasLastName = user.lastName && user.lastName.trim() !== '';

                  // Try to get name from firstName and lastName
                  if (hasFirstName && hasLastName) {
                    return `${user.firstName.trim()} ${user.lastName.trim()}`;
                  }

                  // If only firstName is available
                  if (hasFirstName) {
                    return user.firstName.trim();
                  }

                  // If only lastName is available
                  if (hasLastName) {
                    return user.lastName.trim();
                  }

                  // Fallback to username
                  if (user.username && user.username.trim() !== '') {
                    return user.username.trim();
                  }

                  // Final fallback
                  return 'Utilisateur';
                })() : 'Utilisateur'}
              </h3>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-300">En ligne</span>
              </div>
              {user?.role && (
                <span className="text-xs text-gray-400 capitalize">
                  {user.role.name.replace('_', ' ')}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Collapsed User Profile - Icon Only */}
      {isCollapsed && (
        <div className="p-4 border-b border-gray-300 dark:border-gray-700 flex justify-center">
          <div className="w-10 h-10 bg-primary-purple rounded-full flex items-center justify-center">
            <User className="w-6 h-6 text-white" />
          </div>
        </div>
      )}

      {/* Navigation Menu */}
      <nav className="flex-1 py-4">
        {/* Stats Group */}
        <div className="mb-6">
          {!isCollapsed && (
            <div className="px-6 mb-3">
              <h3 className="font-semibold text-white text-sm uppercase tracking-wider">
                Statistiques
              </h3>
            </div>
          )}
          <div className="space-y-1">
            <Link
              to="/admin"
              className={`flex items-center ${isCollapsed ? 'justify-center px-3' : 'px-6'} py-3 transition-colors duration-200 ${isActive('/admin')
                ? 'bg-vibrant-orange text-white border-r-4 border-white'
                : 'text-white hover:bg-gray-600'
                }`}
              title={isCollapsed ? 'Tableau de bord' : undefined}
            >
              <BarChart3 className="w-5 h-5" />
              {!isCollapsed && <span className="ml-3">Tableau de bord</span>}
            </Link>
          </div>
        </div>

        {/* System Structure Group */}
        <div className="mb-6">
          {!isCollapsed && (
            <div className="px-6 mb-3">
              <h3 className="font-semibold text-white text-sm uppercase tracking-wider">
                Structure du système
              </h3>
            </div>
          )}
          <div className="space-y-1">
            <Link
              to="/admin/categories"
              className={`flex items-center ${isCollapsed ? 'justify-center px-3' : 'px-6'} py-3 transition-colors duration-200 ${isActive('/admin/categories')
                ? 'bg-vibrant-orange text-white border-r-4 border-white'
                : 'text-white hover:bg-gray-600'
                }`}
              title={isCollapsed ? 'Types de services' : undefined}
            >
              <Layers className="w-5 h-5" />
              {!isCollapsed && <span className="ml-3">Types de services</span>}
            </Link>
            <Link
              to="/admin/service-providers"
              className={`flex items-center ${isCollapsed ? 'justify-center px-3' : 'px-6'} py-3 transition-colors duration-200 ${isActive('/admin/service-providers')
                ? 'bg-vibrant-orange text-white border-r-4 border-white'
                : 'text-white hover:bg-gray-600'
                }`}
              title={isCollapsed ? 'Gestion des prestataires' : undefined}
            >
              <Folder className="w-5 h-5" />
              {!isCollapsed && <span className="ml-3">Gestion des prestataires</span>}
            </Link>
            <Link
              to="/admin/services"
              className={`flex items-center ${isCollapsed ? 'justify-center px-3' : 'px-6'} py-3 transition-colors duration-200 ${location.pathname.startsWith('/admin/services')
                ? 'bg-vibrant-orange text-white border-r-4 border-white'
                : 'text-white hover:bg-gray-600'
                }`}
              title={isCollapsed ? 'Gestion des services' : undefined}
            >
              <Users className="w-5 h-5" />
              {!isCollapsed && <span className="ml-3">Gestion des services</span>}
            </Link>
            <Link
              to="/admin/cities"
              className={`flex items-center ${isCollapsed ? 'justify-center px-3' : 'px-6'} py-3 transition-colors duration-200 ${isActive('/admin/cities')
                ? 'bg-vibrant-orange text-white border-r-4 border-white'
                : 'text-white hover:bg-gray-600'
                }`}
              title={isCollapsed ? 'Gestion des villes' : undefined}
            >
              <MapPin className="w-5 h-5" />
              {!isCollapsed && <span className="ml-3">Gestion des villes</span>}
            </Link>
            {inGroup(ACCESS_GROUPS.USER_MANAGERS) && (
              <Link
                to="/admin/users"
                className={`flex items-center ${isCollapsed ? 'justify-center px-3' : 'px-6'} py-3 transition-colors duration-200 ${isActive('/admin/users')
                  ? 'bg-vibrant-orange text-white border-r-4 border-white'
                  : 'text-white hover:bg-gray-600'
                  }`}
                title={isCollapsed ? 'Gestion des utilisateurs' : undefined}
              >
                <UserCheck className="w-5 h-5" />
                {!isCollapsed && <span className="ml-3">Gestion des utilisateurs</span>}
              </Link>
            )}
          </div>
        </div>

        {/* Surveys and Signalements Group - visible to all authenticated admin users (read-only for viewer) */}
        <div className="mb-2">
          {!isCollapsed && (
            <div className="px-6 mb-3">
              <h3 className="font-semibold text-white text-sm uppercase tracking-wider">
                Enquêtes et Signalements
              </h3>
            </div>
          )}
          <div className="space-y-1">
            <Link
              to="/admin/signalements"
              className={`flex items-center ${isCollapsed ? 'justify-center px-3' : 'px-6'} py-3 transition-colors duration-200 ${isActive('/admin/signalements')
                ? 'bg-vibrant-orange text-white border-r-4 border-white'
                : 'text-white hover:bg-gray-600'
                }`}
              title={isCollapsed ? 'Les témoignages' : undefined}
            >
              <AlertTriangle className="w-5 h-5" />
              {!isCollapsed && <span className="ml-3">Les témoignages</span>}
            </Link>
            <Link
              to="/admin/surveys"
              className={`flex items-center ${isCollapsed ? 'justify-center px-3' : 'px-6'} py-3 transition-colors duration-200 ${isActive('/admin/surveys')
                ? 'bg-vibrant-orange text-white border-r-4 border-white'
                : 'text-white hover:bg-gray-600'
                }`}
              title={isCollapsed ? 'Enquêtes' : undefined}
            >
              <FileText className="w-5 h-5" />
              {!isCollapsed && <span className="ml-3">Enquêtes</span>}
            </Link>
          </div>
        </div>

      </nav>



      {/* Admin tools - super_admin only */}
      {inGroup(ACCESS_GROUPS.SUPER_ADMIN_ONLY) && (
        <div className={`${isCollapsed ? 'px-3' : 'px-6'} py-4 border-t border-gray-300 dark:border-gray-700`}>
          {!isCollapsed && (
            <div className="mb-4">
              <h3 className="font-semibold text-white text-sm uppercase tracking-wider mb-3">
                Outils
              </h3>
            </div>
          )}
          <Link
            to="/admin/roles"
            className={`flex items-center ${isCollapsed ? 'justify-center px-3' : 'px-4'} py-3 rounded-lg transition-colors duration-200 ${isActive('/admin/roles')
              ? 'bg-vibrant-orange text-white'
              : 'text-white hover:bg-gray-600'
              }`}
            title={isCollapsed ? 'Gestion des rôles' : undefined}
          >
            <Shield className="w-5 h-5" />
            {!isCollapsed && <span className="ml-3">Gestion des rôles</span>}
          </Link>
          <Link
            to="/admin/data-export"
            className={`flex items-center ${isCollapsed ? 'justify-center px-3' : 'px-4'} py-3 rounded-lg transition-colors duration-200 ${isActive('/admin/data-export')
              ? 'bg-vibrant-orange text-white'
              : 'text-white hover:bg-gray-600'
              }`}
            title={isCollapsed ? 'Export de données' : undefined}
          >
            <Database className="w-5 h-5" />
            {!isCollapsed && <span className="ml-3">Export de données</span>}
          </Link>
        </div>
      )}

      {/* Settings */}
      <div className={`${isCollapsed ? 'px-3' : 'p-6'} border-t border-gray-300 dark:border-gray-700 relative`} ref={dropdownRef}>
        {isCollapsed ? (
          <button
            onClick={() => setIsSettingsDropdownOpen(!isSettingsDropdownOpen)}
            className="flex items-center justify-center w-full p-3 rounded-lg text-gray-300 dark:text-gray-400 hover:text-white dark:hover:text-gray-200 hover:bg-gray-600 transition-colors duration-200"
            title="Paramètres"
          >
            <Settings className="w-5 h-5" />
          </button>
        ) : (
          <button
            onClick={() => setIsSettingsDropdownOpen(!isSettingsDropdownOpen)}
            className="flex items-center justify-between w-full text-gray-300 dark:text-gray-400 hover:text-white dark:hover:text-gray-200 transition-colors duration-200"
          >
            <div className="flex items-center">
              <Settings className="w-5 h-5 mr-3" />
              Paramètres
            </div>
            <ChevronDown
              className={`w-4 h-4 transition-transform duration-200 ${isSettingsDropdownOpen ? 'rotate-180' : ''
                }`}
            />
          </button>
        )}

        {/* Dropdown Menu */}
        {isSettingsDropdownOpen && (
          <div className={`absolute ${isCollapsed ? 'bottom-full left-3 right-3' : 'bottom-full left-6 right-6'} mb-2 bg-white dark:bg-dark-charcoal rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50`}>
            <button
              onClick={toggleTheme}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-gray transition-colors duration-200"
            >
              {theme === 'dark' ? (
                <>
                  <Sun className="w-4 h-4 mr-3" />
                  Mode clair
                </>
              ) : (
                <>
                  <Moon className="w-4 h-4 mr-3" />
                  Mode sombre
                </>
              )}
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-gray transition-colors duration-200"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Déconnexion
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
